import React, {FC, useState} from 'react'
import clsx from "clsx";
import dayjs from "dayjs";
import {Button} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FullscreenModal from "components/FullscreenModal";
import styles from "./components.module.scss";
import Api from "../../../../api";
import FixedLoader from "../../../../components/FixedLoader/FixedLoader";
import {SimpleModal} from "../../../../components/Modal";
import {CrossButtonBlack} from "../../../../components/Buttons/CrossButton";
import {AddPosition} from "../../../landing/SignUp2_Talent/components/AddPosition";
import {HYGIENIST_TYPES, PositionProps} from "../../../../utils/types";
import {StyledButton} from "../../../../components/Buttons/DefaultButton/DefaultButton.styles";

interface ExperienceProps {
    country: 0 | 1,
    isOpen: boolean,
    onClose: () => void,
    positionsObj: PositionProps[],
    setPositionsObj: (data: PositionProps[]) => void,
    isHygienistOrAssistant?: boolean
}

const Experience: FC<ExperienceProps> = (
    {
        country,
        isOpen,
        onClose,
        positionsObj,
        setPositionsObj,
        isHygienistOrAssistant = true
    }) => {
    const [isAddPositionOpen, setIsAddPositionOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [selectedPosition, setSelectedPosition] = useState<PositionProps | null>(null)
    const [onExitModal, setOnExitModal] = useState(false)
    const [onDelete, setOnDelete] = useState(false)
    const [positionToDelete, setPositionToDelete] = useState<PositionProps | null>(null)
    const [showValidationModal, setShowValidationModal] = useState(false);

    const getWorkExperience = async () => {
        const workExperience = await Api.getWorkExperience()
        if (workExperience.length) {
            setPositionsObj(workExperience)
        }
    }

    const addPosition = (data: PositionProps) => {
        try {
            const updatedPositions = positionsObj.filter((item: any) => item !== selectedPosition)
            setIsAddPositionOpen(false)
            setPositionsObj([...updatedPositions, data])
        } catch (e) {
            console.log('Error adding position', e)
        }
    }

    const onSubmit = async (event?: Event) => {
        event?.preventDefault()
        
        if (positionsObj.length === 0) {
            setShowValidationModal(true);
            return;
        }

        try {
            const formattedPositions = positionsObj.map(position => ({
                ...position,
                fromDate: dayjs(position.fromDate).format('YYYY-MM-DD'),
                toDate: dayjs(position.toDate).format('YYYY-MM-DD')
            }))
            
            setLoading(true)
            await Api.addWorkExperience(formattedPositions)
            setLoading(false)
            onClose()
        } catch (e) {
            console.log('Error submitting work experience', e)
            setLoading(false)
        }
    }

    const openPosition = (position: PositionProps | null) => {
        setSelectedPosition(position)
        setIsAddPositionOpen(true)
    }

    const onExit = () => {
        setOnExitModal(true)
    }

    const exitWOSaving = () => {
        if (positionsObj.length === 0) {
            getWorkExperience();
        }
        setOnExitModal(false);
        onClose();
    }

    const exitWithSaving = () => {
        if (positionsObj.length === 0) {
            setShowValidationModal(true);
            setOnExitModal(false);
            return;
        }
        onSubmit().then(() => setOnExitModal(false))
    }

    const onDeletePosition = async () => {
        setPositionsObj(positionsObj.filter((item: any) => item !== positionToDelete))
        setOnDelete(false)
    }

    const deletePosition = (position: PositionProps) => {
        setOnDelete(true)
        setPositionToDelete(position)
    }

    const formatDisplayDate = (date: any) => {
        if (!date) return '';
        return dayjs(date).isValid() ? dayjs(date).format('MMM YYYY') : '';
    }

    return (
        <>
            <SimpleModal isOpen={showValidationModal}>
                <>
                    <div className={styles.titleContainer}>
                        <div className={styles.popupTitle}>Work Experience Required</div>
                        <CrossButtonBlack 
                            className={clsx("no-fastclick", styles.crossButtonDark)}
                            onClick={() => setShowValidationModal(false)}
                        />
                    </div>
                    <div className={styles.description}>
                        You must have at least one work experience entry to be able to send in an offer
                    </div>
                    <StyledButton
                        variant="outlined"
                        size="large"
                        onClick={() => setShowValidationModal(false)}
                    >
                        Okay
                    </StyledButton>
                </>
            </SimpleModal>

            <SimpleModal isOpen={onExitModal}>
                <>
                    <div className={styles.titleContainer}>
                        <div className={styles.popupTitle}>Work Experience</div>
                        <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)}
                                          onClick={() => setOnExitModal(false)}/>
                    </div>
                    <div className={styles.description}>
                        Please remember to save your changes before exiting.
                    </div>
                    <StyledButton
                        variant="outlined"
                        size="large"
                        onClick={exitWithSaving}
                    >
                        Save and Exit
                    </StyledButton>
                    <StyledButton
                        variant="outlined"
                        size="large"
                        onClick={exitWOSaving}
                    >
                        Exit without saving
                    </StyledButton>
                </>
            </SimpleModal>

            <SimpleModal isOpen={onDelete}>
                <>
                    <div className={styles.titleContainer}>
                        <div className={styles.popupTitle}>Delete this position?</div>
                        <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)}
                                          onClick={() => setOnDelete(false)}/>
                    </div>
                    <div className={styles.description}>
                        Please remember to save your changes before exiting.
                    </div>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={onDeletePosition}
                        className={clsx("bigButtonInNewStyle submitButton")}
                    >
                        Delete
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setOnDelete(false)}
                        className={clsx("bigButtonInNewStyle")}
                    >
                        Cancel
                    </Button>
                </>
            </SimpleModal>

            {isAddPositionOpen ? <AddPosition
                country={country}
                position={selectedPosition}
                onAdd={addPosition}
                isOpen={isAddPositionOpen}
                onClose={() => setIsAddPositionOpen(false)}
            /> : null}
            <FullscreenModal
                title={'Work Experience'}
                isOpen={isOpen && !isAddPositionOpen}
                buttonText={'Save'}
                onClose={onExit}
                onSubmit={onSubmit}
            >

                <div className={styles.roundedBox}>
                    <h4>Showcase your professional journey!</h4>
                    <p>Dental Offices really love to see your past work experience when reviewing offers for Temping Shifts and applications for Job Board postings.</p>
                    <p>Newer grad? No worries! Even previous  jobs outside of dentistry, or volunteer work count and are great to highlight. They show your work ethic and skills. </p> 
                    <p>You need at least 1 position entered in this section, but more is better.  Every bit of experience helps you stand out. Let's help your profile shine!</p>
                </div>

                {positionsObj?.map((position: PositionProps, index: number) => {
                    return (
                        <div key={index} className={clsx("list-block inset inputBox", styles.position)}>
                            <div className={styles.positionBox} onClick={() => openPosition(position)}>
                                <div className={styles.positionTitle}>{position.jobTitle}</div>
                                <div className={styles.positionCompany}>{position.companyName}</div>
                                <div className={styles.positionDates}>
                                    {formatDisplayDate(position.fromDate)} - {formatDisplayDate(position.toDate)}
                                </div>
                            </div>
                            <DeleteOutlineOutlinedIcon
                                className={styles.deleteIcon}
                                onClick={() => deletePosition(position)}
                            />
                        </div>
                    )
                })}
                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => openPosition(null)}
                    className={clsx("bigButtonInNewStyle", styles.addPositionButton)}
                >
                    <AddIcon className={styles.addIcon}/>
                    Add Position
                </Button>
            </FullscreenModal>
            <FixedLoader loading={loading} />
        </>
    )
}

export default Experience
